/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

export type PublishStatusResult = {
  /**
   * Slug of the generated Claude Code observability plugin in the published marketplace — install as `<slug>@<marketplace name>`. Present when connected.
   */
  claudeObservabilityPlugin?: string | undefined;
  /**
   * Slug of the generated Codex observability plugin in the published marketplace — install as `<slug>@<marketplace name>`. Present when connected.
   */
  codexObservabilityPlugin?: string | undefined;
  /**
   * Whether GitHub publishing is configured on the server.
   */
  configured: boolean;
  /**
   * Whether this project has a GitHub connection.
   */
  connected: boolean;
  /**
   * Slug of the generated Cursor observability plugin in the published marketplace — the value to mark required in Cursor's team marketplace. Present when connected.
   */
  cursorObservabilityPlugin?: string | undefined;
  /**
   * Whether the repo has at least one directly-added GitHub collaborator (excludes access granted via org membership/teams). Absent when the project is not connected.
   */
  hasCollaborators?: boolean | undefined;
  /**
   * When the project was last published to GitHub. Absent when the project is not connected.
   */
  lastPublishedAt?: Date | undefined;
  /**
   * Version stamped into the currently published plugin.json manifests (e.g. 0.1.1783692954) — the version plugin clients such as Claude Code report for installed plugins. Absent when the project is not connected or the live version could not be determined.
   */
  liveVersion?: string | undefined;
  /**
   * Git-based Claude Code marketplace URL — the value to pass to `/plugin marketplace add` or set as the source URL in `extraKnownMarketplaces`. Present once a marketplace token has been minted, which happens automatically on the first publish.
   */
  marketplaceUrl?: string | undefined;
  /**
   * GitHub repo name, if connected.
   */
  repoName?: string | undefined;
  /**
   * GitHub repo owner, if connected.
   */
  repoOwner?: string | undefined;
  /**
   * Full GitHub repository URL, if connected.
   */
  repoUrl?: string | undefined;
  /**
   * Whether the project's current plugin state matches what was last published to GitHub. Absent when the project is not connected, or when the connection predates content fingerprinting (freshness can't be determined).
   */
  upToDate?: boolean | undefined;
};

/** @internal */
export const PublishStatusResult$inboundSchema: z.ZodMiniType<
  PublishStatusResult,
  unknown
> = z.pipe(
  z.object({
    claude_observability_plugin: z.optional(z.string()),
    codex_observability_plugin: z.optional(z.string()),
    configured: z.boolean(),
    connected: z.boolean(),
    cursor_observability_plugin: z.optional(z.string()),
    has_collaborators: z.optional(z.boolean()),
    last_published_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    live_version: z.optional(z.string()),
    marketplace_url: z.optional(z.string()),
    repo_name: z.optional(z.string()),
    repo_owner: z.optional(z.string()),
    repo_url: z.optional(z.string()),
    up_to_date: z.optional(z.boolean()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "claude_observability_plugin": "claudeObservabilityPlugin",
      "codex_observability_plugin": "codexObservabilityPlugin",
      "cursor_observability_plugin": "cursorObservabilityPlugin",
      "has_collaborators": "hasCollaborators",
      "last_published_at": "lastPublishedAt",
      "live_version": "liveVersion",
      "marketplace_url": "marketplaceUrl",
      "repo_name": "repoName",
      "repo_owner": "repoOwner",
      "repo_url": "repoUrl",
      "up_to_date": "upToDate",
    });
  }),
);

export function publishStatusResultFromJSON(
  jsonString: string,
): SafeParseResult<PublishStatusResult, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => PublishStatusResult$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'PublishStatusResult' from JSON`,
  );
}
